import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  TableContainer,
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@material-ui/core";
import {
  Search,
  Add,
  Edit,
  Delete,
  Sync,
  Refresh,
  FilterList,
  Visibility,
  PlayArrow,
  Phone,
  Link as LinkIcon,
  Reply,
  Code,
  Close,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TemplateModal from "../../components/TemplateModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(3),
    },
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    flexWrap: "wrap",
    alignItems: "stretch",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  searchField: {
    flex: "1 1 auto",
    minWidth: 200,
    [theme.breakpoints.up("sm")]: {
      minWidth: 300,
    },
    [theme.breakpoints.up("md")]: {
      flex: "1 1 40%",
    },
  },
  filterSelect: {
    minWidth: 140,
    flex: "0 1 auto",
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
    [theme.breakpoints.up("sm")]: {
      minWidth: 160,
    },
  },
  clearFilterButton: {
    alignSelf: "center",
    [theme.breakpoints.down("sm")]: {
      alignSelf: "stretch",
    },
  },
  tableContainer: {
    maxHeight: "calc(100vh - 280px)",
    overflowX: "auto",
    overflowY: "auto",
    [theme.breakpoints.down("md")]: {
      maxHeight: "calc(100vh - 320px)",
    },
    [theme.breakpoints.down("sm")]: {
      maxHeight: "calc(100vh - 380px)",
    },
  },
  table: {
    minWidth: 800,
    [theme.breakpoints.down("sm")]: {
      minWidth: 600,
    },
  },
  tableHead: {
    backgroundColor: theme.palette.background.default,
    "& th": {
      fontWeight: 700,
      fontSize: "0.875rem",
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.5),
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(1),
        fontSize: "0.8rem",
      },
    },
  },
  tableCell: {
    padding: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
      fontSize: "0.8rem",
    },
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "0.7rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    minWidth: 80,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.65rem",
      minWidth: 70,
    },
  },
  approved: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  pending: {
    backgroundColor: "#ff9800",
    color: "#fff",
  },
  rejected: {
    backgroundColor: "#f44336",
    color: "#fff",
  },
  categoryChip: {
    backgroundColor: "#2196f3",
    color: "#fff",
    fontWeight: 500,
    fontSize: "0.65rem",
    textTransform: "uppercase",
    minWidth: 70,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.6rem",
      minWidth: 60,
    },
  },
  nameCell: {
    maxWidth: 200,
    minWidth: 120,
    [theme.breakpoints.up("md")]: {
      maxWidth: 250,
    },
  },
  previewCell: {
    maxWidth: 250,
    minWidth: 150,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "0.875rem",
    [theme.breakpoints.up("md")]: {
      maxWidth: 350,
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: 150,
      fontSize: "0.8rem",
    },
  },
  channelCell: {
    minWidth: 100,
    [theme.breakpoints.down("sm")]: {
      minWidth: 80,
    },
  },
  metaIdCell: {
    minWidth: 100,
    maxWidth: 150,
    [theme.breakpoints.down("sm")]: {
      minWidth: 80,
      maxWidth: 100,
    },
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(0.5),
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "nowrap",
  },
  syncButton: {
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      marginTop: theme.spacing(1),
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(8, 3),
    },
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(2),
    opacity: 0.5,
    [theme.breakpoints.down("sm")]: {
      fontSize: 36,
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
    minHeight: 100,
  },
  tableRow: {
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  headerButtons: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 auto",
      },
    },
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_TEMPLATES") {
    const templates = action.payload;
    const newTemplates = [];

    templates.forEach((template) => {
      const templateIndex = state.findIndex((t) => t.id === template.id);
      if (templateIndex !== -1) {
        state[templateIndex] = template;
      } else {
        newTemplates.push(template);
      }
    });

    return [...state, ...newTemplates];
  }

  if (action.type === "UPDATE_TEMPLATE") {
    const template = action.payload;
    const templateIndex = state.findIndex((t) => t.id === template.id);

    if (templateIndex !== -1) {
      state[templateIndex] = template;
      return [...state];
    } else {
      return [template, ...state];
    }
  }

  if (action.type === "DELETE_TEMPLATE") {
    const templateId = action.payload;
    const templateIndex = state.findIndex((t) => t.id === templateId);
    if (templateIndex !== -1) {
      state.splice(templateIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const WhatsAppTemplates = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [templates, dispatch] = useReducer(reducer, []);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [connections, setConnections] = useState([]);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showAllOptions, setShowAllOptions] = useState(false);

  // Fetch conexões WhatsApp
  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const { data } = await api.get("/whatsapp", {
          params: { session: 0 },
        });
        setConnections(data.filter((w) => w.channel === "whatsapp_oficial"));
      } catch (err) {
        console.error("Erro ao carregar conexões:", err);
      }
    };
    fetchConnections();
  }, []);

  // Fetch templates com debounce
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp-templates", {
        params: { searchParam, pageNumber, statusFilter, categoryFilter, channelFilter },
      });
      dispatch({ type: "LOAD_TEMPLATES", payload: data.templates || [] });
      setHasMore(data.hasMore || false);
    } catch (err) {
      console.error("Erro ao carregar templates:", err);
      toast.error("Erro ao carregar templates");
    } finally {
      setLoading(false);
    }
  }, [searchParam, pageNumber, statusFilter, categoryFilter, channelFilter]);

  // Reset ao mudar filtros
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, statusFilter, categoryFilter, channelFilter]);

  // Fetch com debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchTemplates();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchTemplates]);

  // Socket para atualizações em tempo real
  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-template`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TEMPLATE", payload: data.template });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_TEMPLATE", payload: data.templateId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [user.companyId]);

  const handleOpenTemplateModal = useCallback(() => {
    setSelectedTemplate(null);
    setTemplateModalOpen(true);
  }, []);

  const handleCloseTemplateModal = useCallback(() => {
    setSelectedTemplate(null);
    setTemplateModalOpen(false);
    fetchTemplates(); // Recarrega após fechar
  }, [fetchTemplates]);

  const handleEditTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    setTemplateModalOpen(true);
  }, []);

  const handleDeleteTemplate = useCallback(async (templateId) => {
    try {
      await api.delete(`/whatsapp-templates/${templateId}`);
      toast.success(i18n.t("templates.toasts.deleted"));
      dispatch({ type: "DELETE_TEMPLATE", payload: templateId });
    } catch (err) {
      console.error("Erro ao deletar template:", err);
      toast.error(err.response?.data?.error || "Erro ao deletar template");
    } finally {
      setDeletingTemplate(null);
      setConfirmModalOpen(false);
    }
  }, []);

  const handleSyncTemplates = useCallback(async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/whatsapp-templates/sync");
      toast.success(`${data.count || 0} templates sincronizados com sucesso!`);
      dispatch({ type: "RESET" });
      dispatch({ type: "LOAD_TEMPLATES", payload: data.templates || [] });
    } catch (err) {
      console.error("Erro ao sincronizar:", err);
      toast.error(err.response?.data?.error || "Erro ao sincronizar templates");
    } finally {
      setSyncing(false);
    }
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchParam("");
    setStatusFilter("");
    setCategoryFilter("");
    setChannelFilter("");
  }, []);

  const getStatusLabel = (status) => {
    const statusMap = {
      APPROVED: "Aprovado",
      PENDING: "Pendente",
      REJECTED: "Rejeitado",
    };
    return statusMap[status] || status;
  };

  const getStatusClass = (status) => {
    const classMap = {
      APPROVED: classes.approved,
      PENDING: classes.pending,
      REJECTED: classes.rejected,
    };
    return classMap[status] || "";
  };

  return (
    <MainContainer>
      <TemplateModal
        open={templateModalOpen}
        onClose={handleCloseTemplateModal}
        templateId={selectedTemplate?.id}
      />
      <ConfirmationModal
        title={i18n.t("templates.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteTemplate(deletingTemplate.id)}
      >
        {i18n.t("templates.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <MainHeader>
        <Title>Templates WhatsApp</Title>
        <MainHeaderButtonsWrapper>
          <Box className={classes.headerButtons}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Sync />}
              onClick={handleSyncTemplates}
              disabled={syncing}
              size="medium"
            >
              {syncing ? "Sincronizando..." : "Sincronizar"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleOpenTemplateModal}
              size="medium"
            >
              Novo Template
            </Button>
          </Box>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Box className={classes.searchContainer}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar template..."
            type="search"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl className={classes.filterSelect} variant="outlined" size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="APPROVED">Aprovado</MenuItem>
              <MenuItem value="PENDING">Pendente</MenuItem>
              <MenuItem value="REJECTED">Rejeitado</MenuItem>
            </Select>
          </FormControl>
          <FormControl className={classes.filterSelect} variant="outlined" size="small">
            <InputLabel>Categoria</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              label="Categoria"
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="MARKETING">Marketing</MenuItem>
              <MenuItem value="UTILITY">Utilidade</MenuItem>
              <MenuItem value="AUTHENTICATION">Autenticação</MenuItem>
            </Select>
          </FormControl>
          <FormControl className={classes.filterSelect} variant="outlined" size="small">
            <InputLabel>Canal</InputLabel>
            <Select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              label="Canal"
            >
              <MenuItem value="">Todos</MenuItem>
              {connections.map((conn) => (
                <MenuItem key={conn.id} value={conn.name}>
                  {conn.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(searchParam || statusFilter || categoryFilter || channelFilter) && (
            <Tooltip title="Limpar filtros">
              <IconButton 
                size="small" 
                onClick={handleClearFilters} 
                color="primary"
                className={classes.clearFilterButton}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <TableContainer className={classes.tableContainer}>
          <Table size="small" stickyHeader className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell align="left">Nome</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Categoria</TableCell>
                <TableCell align="center">Canal</TableCell>
                <TableCell align="left">Preview</TableCell>
                <TableCell align="center">ID Meta</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && templates.length === 0 ? (
                <TableRowSkeleton columns={7} />
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box className={classes.emptyState}>
                      <FilterList className={classes.emptyIcon} />
                      <Typography variant="h6" gutterBottom>
                        Nenhum template encontrado
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {searchParam || statusFilter || categoryFilter
                          ? "Tente ajustar os filtros de busca"
                          : "Crie seu primeiro template para começar"}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => (
                  <TableRow key={template.id} className={classes.tableRow}>
                    <TableCell align="left" className={`${classes.tableCell} ${classes.nameCell}`}>
                      <Tooltip title={template.name}>
                        <Typography variant="body2" noWrap>
                          {template.name}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" className={classes.tableCell}>
                      <Chip
                        label={getStatusLabel(template.status)}
                        className={`${classes.statusChip} ${getStatusClass(
                          template.status
                        )}`}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center" className={classes.tableCell}>
                      <Chip
                        label={template.category}
                        className={classes.categoryChip}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center" className={`${classes.tableCell} ${classes.channelCell}`}>
                      <Typography variant="body2" noWrap>
                        {template.channelName || template.channel || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="left" className={`${classes.tableCell} ${classes.previewCell}`}>
                      {template.bodyText ? (
                        <Tooltip title={template.bodyText}>
                          <Typography variant="body2" noWrap>
                            {template.bodyText}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" style={{ color: "#999", fontStyle: "italic" }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" className={`${classes.tableCell} ${classes.metaIdCell}`}>
                      {template.metaId ? (
                        <Tooltip title={template.metaId} arrow placement="top">
                          <Typography 
                            variant="caption" 
                            noWrap
                            style={{ 
                              cursor: "help",
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                              display: "inline-block",
                              maxWidth: "100%"
                            }}
                          >
                            {template.metaId}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" style={{ color: "#999" }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" className={classes.tableCell}>
                      <Box className={classes.actionButtons}>
                        <Tooltip title="Visualizar">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setPreviewTemplate(template);
                              setPreviewModalOpen(true);
                            }}
                            style={{ color: "#0088cc" }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEditTemplate(template)}
                            color="primary"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeletingTemplate(template);
                              setConfirmModalOpen(true);
                            }}
                            color="secondary"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {loading && templates.length > 0 && (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Botão Carregar Mais */}
        {!loading && hasMore && templates.length > 0 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setPageNumber((prev) => prev + 1)}
              startIcon={<Refresh />}
            >
              Carregar Mais Templates
            </Button>
          </Box>
        )}
      </Paper>

      <TemplateModal
        open={templateModalOpen}
        onClose={() => {
          setTemplateModalOpen(false);
          setSelectedTemplate(null);
        }}
        templateId={selectedTemplate?.id}
      />

      <ConfirmationModal
        title="Excluir Template"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteTemplate(deletingTemplate?.id)}
      >
        Tem certeza que deseja excluir o template "{deletingTemplate?.name}"?
      </ConfirmationModal>

      {/* Modal de Preview */}
      <Dialog
        open={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setShowAllOptions(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            minHeight: showAllOptions ? "600px" : "auto",
            position: "relative",
          },
        }}
      >
        <DialogTitle style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px" }}>
          <Typography variant="h6" style={{ fontSize: "1rem", fontWeight: 600 }}>
            Prévia do modelo
          </Typography>
          <IconButton 
            onClick={() => {
              setPreviewModalOpen(false);
              setShowAllOptions(false);
            }} 
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: 0, position: "relative", overflow: "hidden" }}>
          {previewTemplate && (
            <>
              <Paper
                style={{
                  backgroundColor: "#a39e93",
                  padding: 24,
                  minHeight: 400,
                  position: "relative",
                }}
                elevation={0}
              >
                <Paper
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: 12,
                    padding: "12px 16px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    maxWidth: 280,
                  }}
                >
                  {previewTemplate.headerText && (
                    <Typography style={{ fontWeight: 600, fontSize: "0.95rem", marginBottom: 8 }}>
                      {previewTemplate.headerText}
                    </Typography>
                  )}
                  {previewTemplate.bodyText && (
                    <Typography style={{ fontSize: "0.875rem", whiteSpace: "pre-wrap", marginBottom: 4 }}>
                      {previewTemplate.bodyText}
                    </Typography>
                  )}
                  {previewTemplate.footerText && (
                    <Typography style={{ fontSize: "0.75rem", color: "#8696a0", marginTop: 8 }}>
                      {previewTemplate.footerText}
                    </Typography>
                  )}
                  <Typography style={{ fontSize: "0.6875rem", color: "#8696a0", textAlign: "right", marginTop: 4 }}>
                    {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                  {previewTemplate.buttons && previewTemplate.buttons.length > 0 && !showAllOptions && (
                    <Box mt={1}>
                      {previewTemplate.buttons.slice(0, 2).map((button, index) => (
                        <Box
                          key={index}
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          style={{
                            color: "#0088cc",
                            fontSize: "0.875rem",
                            marginTop: 4,
                            cursor: "pointer",
                          }}
                        >
                          {button.type === "PHONE_NUMBER" && <Phone style={{ fontSize: 16 }} />}
                          {button.type === "URL" && <LinkIcon style={{ fontSize: 16 }} />}
                          {button.type === "QUICK_REPLY" && <Reply style={{ fontSize: 16 }} />}
                          {button.type === "COPY_CODE" && <Code style={{ fontSize: 16 }} />}
                          <span>{button.text}</span>
                        </Box>
                      ))}
                      {previewTemplate.buttons.length > 2 && (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={0.5}
                          onClick={() => setShowAllOptions(true)}
                          style={{
                            color: "#0088cc",
                            fontSize: "0.875rem",
                            marginTop: 8,
                            cursor: "pointer",
                            padding: "4px 0",
                          }}
                        >
                          <Reply style={{ fontSize: 16 }} />
                          <span>Ver todas as opções</span>
                        </Box>
                      )}
                    </Box>
                  )}
                </Paper>
              </Paper>

              {/* Painel "Todas as opções" */}
              {showAllOptions && previewTemplate.buttons && previewTemplate.buttons.length > 0 && (
                <Paper
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: "#fff",
                    borderRadius: "16px 16px 0 0",
                    padding: "16px 24px 24px",
                    boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
                    animation: "slideUp 0.3s ease-out",
                  }}
                >
                  <Box display="flex" justifyContent="center" mb={1}>
                    <div style={{ width: 40, height: 4, backgroundColor: "#ddd", borderRadius: 2 }} />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                      Todas as opções
                    </Typography>
                    <IconButton size="small" onClick={() => setShowAllOptions(false)}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box>
                    {previewTemplate.buttons.map((button, index) => (
                      <Box
                        key={index}
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                        style={{
                          padding: "12px 0",
                          borderBottom: index < previewTemplate.buttons.length - 1 ? "1px solid #f0f0f0" : "none",
                          cursor: "pointer",
                        }}
                      >
                        {button.type === "PHONE_NUMBER" && <Phone style={{ fontSize: 20, color: "#666" }} />}
                        {button.type === "URL" && <LinkIcon style={{ fontSize: 20, color: "#666" }} />}
                        {button.type === "QUICK_REPLY" && <Reply style={{ fontSize: 20, color: "#666" }} />}
                        {button.type === "COPY_CODE" && <Code style={{ fontSize: 20, color: "#666" }} />}
                        <Typography style={{ fontSize: "0.9375rem", color: "#000" }}>
                          {button.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </MainContainer>
  );
};

export default WhatsAppTemplates;
